/* cSpell:ignore libphonenumber */

import React, { createContext, useCallback, useContext } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import {
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  isValid,
} from 'date-fns';
import { formatPhoneNumber, logger as LOG } from '@sweetgreen/garnish';

import type { TranslationKeys } from './locales';
import { enUs, es } from './locales';

export type FormatXMLElementFn<
  T,
  R = string | T | ReadonlyArray<string | T>,
> = (parts: ReadonlyArray<string | T>) => R;
export type PrimitiveType = string | number | boolean | null | undefined | Date;
export type TranslationValues = Record<
  string,
  PrimitiveType | FormatXMLElementFn<string, string>
>;
export type LocaleNames = 'en-US' | 'es';
export type Locale = {
  readonly label: LocaleNames;
  readonly translation: typeof enUs | typeof es;
};

// NOTE: Purposely separating currency from locale since
// locale should only govern which translation should be used
// and how the values are formatted, but NOT the currency with
// which the transaction is made.
export type SupportedCurrencies = 'USD'; // We only support USD for the time being
export type SupportedLocales = Readonly<Record<string, Locale>>;

export const supportedLocales: SupportedLocales = {
  enUs: {
    label: 'en-US',
    translation: enUs,
  },
  es: {
    label: 'es',
    translation: es,
  },
};

export type LocalizationProviderInterface = {
  readonly children: React.ReactElement;
};

export type LocalizationContextInterface = {
  readonly setCurrentLocale: React.Dispatch<React.SetStateAction<Locale>>;
  readonly currentLocale: Locale;
};

export const LocalizationContext = createContext<LocalizationContextInterface>({
  currentLocale: supportedLocales.enUs,
  setCurrentLocale: () => null,
});

export const useLocalizationContext = () => {
  const { currentLocale, setCurrentLocale } = useContext(LocalizationContext);
  const intl = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const formatMessage = useCallback(
    (key: TranslationKeys, values?: TranslationValues): string =>
      String(
        intl.formatMessage(
          {
            id: key,
            defaultMessage: 'Missing Translation',
          },
          values,
        ),
      ),
    [intl],
  );

  const formatPrice = useCallback(
    (
      value: FormatCurrencyParameter,
      currency: SupportedCurrencies,
    ): FormatCurrencyReturn =>
      String(
        intl.formatNumber(Number(value) / 100, {
          style: 'currency',
          currency: String(currency),
        }),
      ),
    [intl],
  );

  const formatDate = useCallback(
    (...params: Parameters<typeof intl.formatDate>) => {
      return intl.formatDate(...params);
    },
    [intl],
  );

  const getDistanceBetweenDatesInReadableFormat = useCallback(
    (params: GetDatesDifferenceInReadableFormatParams) => {
      const { numericDifference, dateUnit, dateUnitPlural } = params;

      const formattedDateUnit =
        numericDifference > 1 ? dateUnitPlural : dateUnit;

      return formatMessage('date.date-distance', {
        numericDate: numericDifference,
        dateUnit: formattedDateUnit,
      });
    },
    [formatMessage],
  );

  /**
   * Returns "distance" between 2 dates in a human-readable format
   * (e.g. "in 10 seconds", "in 1 day" ...).
   *
   * @example
   *
   * const { formatDistanceBetweenDates } = useLocalizationProvider();
   *
   * formatDistanceBetweenDates({
   *   baseDate: new Date(), // current date by default
   *   targetDate: addDays(new Date(), 10)
   * }) => in 10 days
   *
   * NOTE: We use our own implementation, because `IntlDateFormatIntl.DateTimeFormat`
   *       is not completely supported by React Native,
   */
  const formatDistanceBetweenDates = useCallback(
    (props: FormatDistanceBetweenDatesParams): string => {
      const { baseDate = new Date(), targetDate } = props;

      // ─── Guards ──────────────────────────────────

      const areValidDatesProvided = isValid(baseDate) && isValid(targetDate);

      if (!areValidDatesProvided) {
        log.debug(
          `Provided valid dates (baseDate: \`${baseDate}\`, targetDate: \`${targetDate}\`)`,
        );

        return '';
      }

      // ─────────────────────────────────────────────

      const [
        diffInYears,
        diffInMonths,
        diffInDays,
        diffInHours,
        diffInMinutes,
        diffInSeconds,
        diffInMilliseconds,
      ] = [
        differenceInYears(targetDate, baseDate),
        differenceInMonths(targetDate, baseDate),
        differenceInDays(targetDate, baseDate),
        differenceInHours(targetDate, baseDate),
        differenceInMinutes(targetDate, baseDate),
        differenceInSeconds(targetDate, baseDate),
        differenceInMilliseconds(targetDate, baseDate),
      ];

      // ─── Years ───────────────────────────────────

      if (diffInYears > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInYears,
          dateUnit: formatMessage('date.year'),
          dateUnitPlural: formatMessage('date.years'),
        });
      }

      // ─── Months ──────────────────────────────────

      if (diffInMonths > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInMonths,
          dateUnit: formatMessage('date.month'),
          dateUnitPlural: formatMessage('date.months'),
        });
      }

      // ─── Days ────────────────────────────────────

      if (diffInDays > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInDays,
          dateUnit: formatMessage('date.day'),
          dateUnitPlural: formatMessage('date.days'),
        });
      }

      // ─── Hours ───────────────────────────────────

      if (diffInHours > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInHours,
          dateUnit: formatMessage('date.hour'),
          dateUnitPlural: formatMessage('date.hours'),
        });
      }

      // ─── Minutes ─────────────────────────────────

      if (diffInMinutes > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInMinutes,
          dateUnit: formatMessage('date.minute'),
          dateUnitPlural: formatMessage('date.minutes'),
        });
      }

      // ─── Seconds ─────────────────────────────────

      if (diffInSeconds > 0) {
        return getDistanceBetweenDatesInReadableFormat({
          numericDifference: diffInSeconds,
          dateUnit: formatMessage('date.second'),
          dateUnitPlural: formatMessage('date.seconds'),
        });
      }

      // ─── Milliseconds ────────────────────────────

      if (diffInMilliseconds > 0) {
        return formatMessage('date.in-a-second');
      }

      return '';
    },
    [formatMessage, getDistanceBetweenDatesInReadableFormat],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    currentLocale,
    formatPlural: intl.formatPlural,
    formatPrice,
    formatDate,
    formatDistanceBetweenDates,
    formatMessage,
    t: formatMessage, // Alias for formatMessage
    formatNumber: intl.formatNumber,
    formatPhoneNumber,
    setCurrentLocale,
  };
};

export const LocalizationProvider = ({
  children,
}: LocalizationProviderInterface) => {
  const [currentLocale, setCurrentLocale] = React.useState<Locale>(
    supportedLocales.enUs,
  );

  const value = React.useMemo(
    () => ({
      setCurrentLocale,
      currentLocale,
    }),
    [currentLocale],
  );

  return (
    <LocalizationContext.Provider value={value}>
      <IntlProvider
        messages={currentLocale.translation}
        locale={currentLocale.label}
        defaultLocale={supportedLocales.enUs.label}
      >
        {children}
      </IntlProvider>
    </LocalizationContext.Provider>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

LOG.enable('LOCALIZATION_PROVIDER');
const log = LOG.extend('LOCALIZATION_PROVIDER');

// ─── Types ───────────────────────────────────────────────────────────────────

type FormatDistanceBetweenDatesParams = {
  readonly baseDate?: Date | number;
  readonly targetDate: Date | number;
};

type FormatCurrencyParameter = Parameters<IntlShape['formatNumber']>[0];

type FormatCurrencyReturn = ReturnType<IntlShape['formatNumber']>[0];

type IntlShape = NonNullable<ReturnType<typeof useIntl>>;

type GetDatesDifferenceInReadableFormatParams = {
  readonly numericDifference: number;
  readonly dateUnit: string;
  readonly dateUnitPlural: string;
};
